import { FC, ReactNode, useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useAppDispatch } from "hooks/useAppDispatch";
import { logoutThunk } from "reduxStore/auth/thunks";
import { acceptTermsOfUse } from "services/accountSession";
import { sessionAcceptedTerms } from "utils/appSessionClaims";

type Props = {
  children: ReactNode;
};

const CONNECTION_MESSAGE =
  "We could not record your acceptance. Please check your connection and try again.";

const EXPIRED_SESSION_MESSAGE =
  "Your session is no longer valid. Sign out and sign in again to accept.";

/**
 * Reports why acceptance failed instead of always blaming the connection: a
 * rejected session needs a new sign-in, not a retry.
 */
function acceptanceErrorMessage(error: unknown): string {
  if (!isAxiosError<{ message?: string }>(error)) {
    return CONNECTION_MESSAGE;
  }
  if (error.response?.status === 401 || error.response?.status === 403) {
    return EXPIRED_SESSION_MESSAGE;
  }
  const data = error.response?.data;
  if (typeof data === "object" && data?.message) {
    return data.message;
  }
  return CONNECTION_MESSAGE;
}

/**
 * Blocks the authenticated app until the Terms of Use are accepted. This is the
 * client half of the gate; the API grants only the restricted `pending_terms`
 * Hasura role until acceptance is recorded, so bypassing this screen does not
 * grant data access.
 */
export const TermsOfUseGate: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [accepted, setAccepted] = useState(() => sessionAcceptedTerms());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptTermsOfUse();
      // The re-issued token carries the profile's real role; a reload rebuilds
      // the Apollo client and Redux state with it.
      window.location.reload();
    } catch (err) {
      console.error("Terms of Use acceptance failed:", err);
      setError(acceptanceErrorMessage(err));
      setSubmitting(false);
    }
  }, []);

  const handleDecline = useCallback(() => {
    setAccepted(false);
    dispatch(logoutThunk());
  }, [dispatch]);

  if (accepted) {
    return <>{children}</>;
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      px={2}
      py={4}
      sx={{ backgroundColor: "background.default" }}
    >
      <Card variant="outlined" sx={{ maxWidth: 560, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Accept the Terms of Use
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Before using VetNebula you must accept our Terms of Use and Privacy
            Policy. Please read them carefully — you cannot access the app until
            you accept.
          </Typography>

          <Box mt={2}>
            <Link href="/terms" target="_blank" rel="noopener" variant="body2">
              Read the Terms of Use
            </Link>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            mt={3}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDecline}
              disabled={submitting}
            >
              Decline and sign out
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAccept}
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={16} /> : undefined
              }
            >
              I Accept
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
